import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'
import ChevronDownIcon from '@heroicons/react/16/solid/ChevronDownIcon'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-gray-tr-08 disabled:text-txt-disabled transition-all',
  {
    variants: {
      variant: {
        avatar: 'w-8 h-8 bg-green-600 text-white child:bg-red-500',
        default: 'bg-primary text-primary-foreground hover:bg-accent-high',
        destructive: 'bg-red-tr-08 text-txt-error hover:bg-red-tr-12',
        destructiveOutline:
          'text-txt-error hover:bg-red-tr-12 border border-error disabled:bg-transaparent disabled:border-primary',
        destructiveGhost:
          'text-txt-error hover:bg-red-tr-12 disabled:bg-transaparent disabled:border-primary',
        outline:
          'border border-primary bg-white hover:bg-accent hover:bg-gray-tr-08  disabled:bg-white text-txt-secondary',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-accent-tr-12',
        ghost:
          'text-txt-secondary hover:bg-accent hover:bg-gray-tr-08 disabled:bg-transaparent',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-7 px-2 py-1 rounded-sm btn-size-sm text-xs',
        md: 'h-8 px-3 py-1.5 rounded-md btn-size-md text-sm',
        lg: 'h-10 px-4 py-2 rounded-lg btn-size-lg text-base',
        // default: 'h-10 px-4 py-2',
        // sm: 'h-9 rounded-md px-3',
        // lg: 'h-11 rounded-md px-8',
        // icon: 'h-10 w-10',
      },
      iconOnly: {
        true: '',
      },
    },
    compoundVariants: [
      {
        size: 'lg',
        iconOnly: true,
        className: 'size-10 p-0',
      },
      {
        size: 'md',
        iconOnly: true,
        className: 'size-8 p-0',
      },
      {
        size: 'sm',
        iconOnly: true,
        className: 'size-6 p-0',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
      iconOnly: false,
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  iconOnly?: boolean
  loading?: boolean
  dropdown?: boolean
}

export interface ButtonTextProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
}

const ButtonText = React.forwardRef<HTMLSpanElement, ButtonTextProps>(
  ({ className, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : 'span'
    return (
      <Comp ref={ref} {...props} className={cn(['button-text'], className)} />
    )
  },
)

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      iconOnly,
      asChild = false,
      loading,
      dropdown,
      children,
      disabled: _disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    const disabled = loading ? true : _disabled
    return (
      <Comp
        data-eslot="button"
        className={cn(buttonVariants({ variant, size, iconOnly, className }))}
        ref={ref}
        disabled={disabled}
        {...props}
      >
        <>
          {loading && (
            <Loader2 className="animate-spin text-[hsl(var(--fill-gray-high))]" />
          )}
          {children}
        </>
        {dropdown && <ChevronDownIcon className="ml-2" />}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants, ButtonText }
